import { useTranslation } from 'react-i18next';
import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { 
  Plus, 
  Save, 
  Trash2, 
  Loader2, 
  CalendarDays,
  FolderOpen,
  Users,
  Clock,
  Edit
} from 'lucide-react';
import { formatDateDDMMYYYY } from '../utils/helpers';

const PlanHeader = ({
  plan,
  hasUnsavedChanges,
  saving,
  defaultWorkHours,
  defaultTravelHours,
  onDefaultWorkHoursChange,
  onDefaultTravelHoursChange,
  onAddEmployee,
  onBulkAddEmployees,
  onBulkHours,
  onAddHoliday,
  onSaveTemplate,
  onLoadTemplate,
  onEditPlan,
  onSave,
  onDelete
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div>
              <CardTitle>{plan.name || plan.project_name}</CardTitle>
              <CardDescription>
                {formatDateDDMMYYYY(plan.start_date)} - {formatDateDDMMYYYY(plan.end_date)}
                {plan.project_country && ` • Country: ${plan.project_country}`}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onEditPlan} title={t('auto.ctr.editPlanDates2')} data-testid="edit-plan-btn">
              <Edit className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* Default Hours Inputs */}
            <div className="flex items-center gap-2 border rounded-md px-2 py-1 bg-gray-50">
              <span className="text-sm text-gray-600">{t('auto.ctr.default')}</span>
              <div className="flex items-center gap-1">
                <Label className="text-xs text-gray-500">W</Label>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={defaultWorkHours}
                  onChange={(e) => onDefaultWorkHoursChange(parseFloat(e.target.value) || 0)}
                  className="h-7 w-14 text-center text-sm"
                  data-testid="default-work-hours"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs text-gray-500">T</Label>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={defaultTravelHours}
                  onChange={(e) => onDefaultTravelHoursChange(parseFloat(e.target.value) || 0)}
                  className="h-7 w-14 text-center text-sm bg-blue-50"
                  data-testid="default-travel-hours"
                />
              </div>
            </div>
            
            {/* Add Employee Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="add-employee-menu">
                  <Plus className="h-4 w-4 mr-2" /> {t('auto.ctr.addEmployee')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={onAddEmployee} data-testid="menu-addemployee">
                  <Plus className="h-4 w-4 mr-2" /> {t('auto.ctr.addSingleEmployee')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onBulkAddEmployees} data-testid="bulk-add-btn">
                  <Users className="h-4 w-4 mr-2" /> {t('auto.ctr.addMultipleEmployees')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Bulk Hours Button */}
            <Button variant="outline" onClick={onBulkHours} data-testid="bulk-hours-btn">
              <Clock className="h-4 w-4 mr-2" /> {t('auto.ctr.bulkHours')}
            </Button>
            
            <Button variant="outline" onClick={onAddHoliday} data-testid="btn-addholiday">
              <CalendarDays className="h-4 w-4 mr-2" /> {t('auto.ctr.nonWorkingDay')}
            </Button>
            
            {/* Template Buttons */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="template-menu-btn">
                  <FolderOpen className="h-4 w-4 mr-2" /> {t('auto.ctr.templates')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={onSaveTemplate} data-testid="menu-savetemplate">
                  <Save className="h-4 w-4 mr-2" /> {t('auto.ctr.saveAsTemplate')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLoadTemplate} data-testid="menu-loadtemplate">
                  <FolderOpen className="h-4 w-4 mr-2" /> {t('auto.ctr.loadTemplate')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button onClick={onSave} disabled={saving || !hasUnsavedChanges} data-testid="btn-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            <Button variant="destructive" size="icon" onClick={onDelete} data-testid="btn-delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

export default PlanHeader;
